import React, { useMemo } from "react";

import { GMN } from "@nens/brostar/openapi-client";
import {
  DeliveryContextGMNOptions,
  translateDeliveryContext,
} from "@nens/brostar/uploadapi-client";
import { ColumnDefinition } from "@nens/chakra/components";

import { getSearchKey } from "@/components/searchbox";
import { TableOverview } from "@/components/table-overview";
import { renderDefault } from "@/helpers/table-render-functions";
import { useAllFetchedPagination } from "@/hooks/use-pagination";
import { KEY_ADDON_MAP, useRouterTableData } from "@/hooks/use-router-table-data";
import { useGmnListAllPagesQuery } from "@/queries/query-gmn";

import { FilterWrapper } from "../../components/filter-wrapper";

import { GmnFilters } from "./components/gmn-filters";
import { GmnMeasuringPointTable } from "./components/gmn-measuring-point-table";
import { gmnInformation } from "./types";

const columnDefinitions: ColumnDefinition<GMN>[] = gmnInformation.map(({ key, label }, index) => ({
  title: label,
  renderFunction: (row) => {
    let renderThis = row[key];

    if (key === "deliveryContext")
      renderThis = translateDeliveryContext(renderThis as DeliveryContextGMNOptions);

    return renderDefault(
      renderThis,
      index === 0 ? `/gmn/startregistratie/${row.uuid}/` : undefined
    );
  },
  width: "1fr",
}));

const GmnTable: React.FC = () => {
  const {
    searchField: _searchField,
    searchString,
    setSearchField,
    setSearchString,
  } = useRouterTableData({ keyAddon: KEY_ADDON_MAP.GMN });
  const searchField = (_searchField as keyof GMN | undefined) || getSearchKey(gmnInformation[0]);
  const { data, isLoading } = useGmnListAllPagesQuery();

  const gmns = useMemo(() => {
    const all = data?.results ?? [];
    if (!searchString) return all;
    return all.filter((gmn) => {
      const part = gmn[searchField];
      if (!part) return true;
      return part.toString().toLowerCase().includes(searchString.toLowerCase());
    });
  }, [data, searchString, searchField]);

  const { paginatedTableData, ...paginationProps } = useAllFetchedPagination(
    KEY_ADDON_MAP.GMN,
    gmns
  );

  return (
    <>
      <FilterWrapper>
        <GmnFilters {...{ searchField, searchString, setSearchField, setSearchString }} />
      </FilterWrapper>
      <TableOverview
        isLoading={isLoading}
        rowAccordion={({ uuid }: GMN) => {
          // FUTURE: if no measuring points, then do not show accordion, as it would be empty
          return <GmnMeasuringPointTable gmn={uuid ?? ""} />;
        }}
        tableData={paginatedTableData}
        columnDefinitions={columnDefinitions}
        paginationProps={paginationProps}
      />
    </>
  );
};

export { GmnTable };
